import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import type { RootState } from '.';
import {
  addAdminMessageFromSocket,
  confirmOptimisticMessage,
  hydrateFromCache,
  invalidateHydratedCache,
  loadOlderSupportMessages,
  loadSupportMessages,
  loadSupportThread,
  prependMessages,
  removeLocalMessage,
  resetSupportChat,
  setMessages,
  threadUpdatedFromSocket,
} from './supportChatSlice';
import { clearAuth } from './authSlice';
import {
  clearAllSupportChatCaches,
  writeSupportChatCache,
} from '../utils/supportChatCache';
import { scheduleSupportMediaCacheSync } from '../utils/supportChatMediaSync';

let persistTimer: ReturnType<typeof setTimeout> | null = null;

const schedulePersist = (listenerApi: { getState: () => unknown }) => {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    const state = listenerApi.getState() as RootState;
    const userId = state.storeAuth.user?.userId;
    const { thread, messages, hasMore } = state.supportChat;

    if (!userId || !thread) return;

    const hasSent = messages.some((m) => m.status === 'sent' && m.id);
    if (!hasSent) return;

    writeSupportChatCache(userId, thread, messages, hasMore);
    scheduleSupportMediaCacheSync(userId, thread.id, messages, hasMore);
  }, 400);
};

export const supportChatCacheListener = createListenerMiddleware();

supportChatCacheListener.startListening({
  matcher: isAnyOf(
    hydrateFromCache,
    invalidateHydratedCache,
    resetSupportChat,
    clearAuth,
    loadSupportMessages.fulfilled,
    loadOlderSupportMessages.fulfilled,
    confirmOptimisticMessage,
    addAdminMessageFromSocket,
    removeLocalMessage,
    setMessages,
    prependMessages,
    threadUpdatedFromSocket,
  ),
  effect: (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const userId = state.storeAuth.user?.userId;

    if (invalidateHydratedCache.match(action) || resetSupportChat.match(action) || clearAuth.match(action)) {
      if (userId != null) {
        clearAllSupportChatCaches(userId);
      }
      return;
    }

    if (threadUpdatedFromSocket.match(action)) {
      const { thread, messages } = state.supportChat;
      if (thread && messages.length === 0) {
        if (userId != null) clearAllSupportChatCaches(userId);
      } else {
        schedulePersist(listenerApi);
      }
      return;
    }

    if (hydrateFromCache.match(action)) {
      const { thread, messages, hasMore } = state.supportChat;
      if (userId != null && thread?.id && messages.length > 0) {
        scheduleSupportMediaCacheSync(userId, thread.id, messages, hasMore);
      }
      return;
    }

    schedulePersist(listenerApi);
  },
});
